import React, { createContext, useContext, useState } from 'react';
import { getPatientVideos, getAllPatientVideos } from '../api/videos';
import { fetchAllTeachers, fetchingTeachersForStudents } from '../api/teachers';
import AuthContext from './AuthContext';
import {
  clearStudentLoginCache,
  patientLogin,
  studentLogin,
} from '../api/auth';
import { fetchPatientTreatments, getMyPatientsDetails } from '../api/patients';
export const DataContext = createContext();

const DataProvider = ({ children }) => {
  const [patientData, setPatientData] = useState([]);
  const [studentData, setStudentData] = useState([]);
  const [currentPatientData, setCurrentPatientData] = useState(null);
  const [currentStudentData, setCurrentStudentData] = useState(null);
  const [currentPatientVideos, setCurrentPatientVideos] = useState(null);
  const [allPatientsAllVideos, setAllPatientsAllVideos] = useState(null);

  // Cache for getAllPatientVideos calls to prevent duplicate API calls
  const [videosCache, setVideosCache] = useState(new Map());
  const [videosCacheTimestamp, setVideosCacheTimestamp] = useState(new Map());
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds - longer cache since no auto-refetch

  // Cache for patients data to prevent duplicate API calls
  const [patientsCache, setPatientsCache] = useState(new Map());
  const [patientsCacheTimestamp, setPatientsCacheTimestamp] = useState(
    new Map()
  );

  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  const { typeOfUser } = useContext(AuthContext);
  // eslint-disable-next-line no-unused-vars
  const [loadingFetchOnePatientData, setLoadingOnePatientData] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [loadingFetchOneStudentData, setLoadingOneStudentData] = useState(true);

  // * Only for doctor login
  // For patient this info is already retrieved during login and kept in user state

  const fetchPatientsForDoctor = async (doctorUid) => {
    const now = Date.now();
    const cachedData = patientsCache.get(doctorUid);
    const cachedTimestamp = patientsCacheTimestamp.get(doctorUid);

    // Check if we have valid cached data
    if (
      cachedData &&
      cachedTimestamp &&
      now - cachedTimestamp < CACHE_DURATION
    ) {
      return cachedData;
    }

    // If no valid cache, fetch from API
    const result = await getMyPatientsDetails(doctorUid);
    const patients = result ? result?.patients : [];

    // Cache the result
    setPatientsCache((prev) => new Map(prev).set(doctorUid, patients));
    setPatientsCacheTimestamp((prev) => new Map(prev).set(doctorUid, now));

    return patients;
  };

  // * Both for doctor and patient logins
  const fetchOnePatientData = async (patientUid) => {
    if (!patientUid) {
      return null;
    }
    
    const treatments_diagnosis = await fetchPatientTreatments(patientUid);
    const patientData = await patientLogin(patientUid);
    
    // Always return a value, even if data is empty
    return {
      data: treatments_diagnosis || [],
      id: patientUid,
      user: patientData?.myDetails || null,
    };
  };

  const fetchOneStudentData = async (studentUid) => {
    if (!studentUid) {
      return null;
    }
    
    let treatments_diagnosis;
    if (typeOfUser === 'TEACHER') {
      treatments_diagnosis = await fetchPatientTreatments(
        studentUid,
        'TEACHER'
      );
    } else {
      treatments_diagnosis = await fetchPatientTreatments(
        studentUid,
        'STUDENT'
      );
    }
    const studentData = await studentLogin(studentUid);
    
    // Always return a value, even if data is empty
    return {
      data: treatments_diagnosis || [],
      id: studentUid,
      user: studentData?.myDetails || null,
    };
  };

  const fetchOnePatientAllVideos = async (patientUid) => {
    const result = await getPatientVideos(patientUid, 'all');

    if (result) return { data: result, id: patientUid };
    else return { data: [], id: patientUid };
  };

  const fetchOneStudentAllVideos = async (studentUid) => {
    const result = await getPatientVideos(studentUid, 'all');

    if (result) return { data: result, id: studentUid };
    else return { data: [], id: studentUid };
  };

  // Cached function to get all videos for a user (works for both doctors and teachers)
  const getCachedAllVideos = async (uid) => {
    const now = Date.now();
    const cachedData = videosCache.get(uid);
    const cachedTimestamp = videosCacheTimestamp.get(uid);

    // Check if we have valid cached data
    if (
      cachedData &&
      cachedTimestamp &&
      now - cachedTimestamp < CACHE_DURATION
    ) {
      return cachedData;
    }

    // If no valid cache, fetch from API
    const result = await getAllPatientVideos(uid);

    // Cache the result
    setVideosCache((prev) => new Map(prev).set(uid, result));
    setVideosCacheTimestamp((prev) => new Map(prev).set(uid, now));

    return result;
  };

  const fetchAllPatientsAllVideos = async (doctorUid) => {
    const result = await getCachedAllVideos(doctorUid);
    if (result) return { data: result, id: doctorUid };
    else return { data: [], id: doctorUid };
  };

  const fetchAllStudentsAllVideos = async (teacherUid) => {
    const result = await getCachedAllVideos(teacherUid);
    if (result) return { data: result, id: teacherUid };
    else return { data: [], id: teacherUid };
  };

  const resetStates = () => {
    setPatientData([]);
    setCurrentPatientData(null);
    setAllPatientsAllVideos(null);
    setCurrentPatientVideos(null);
    // Clear the cache when resetting states
    setVideosCache(new Map());
    setVideosCacheTimestamp(new Map());
    setPatientsCache(new Map());
    setPatientsCacheTimestamp(new Map());
  };

  const resetStudentStates = () => {
    setStudentData([]);
    setCurrentStudentData(null);
    // Clear student login cache when resetting states
    clearStudentLoginCache();
  };

  // Function to clear videos cache for a specific user or all users
  const clearVideosCache = (uid = null) => {
    if (uid) {
      setVideosCache((prev) => {
        const newCache = new Map(prev);
        newCache.delete(uid);
        return newCache;
      });
      setVideosCacheTimestamp((prev) => {
        const newCache = new Map(prev);
        newCache.delete(uid);
        return newCache;
      });
    } else {
      setVideosCache(new Map());
      setVideosCacheTimestamp(new Map());
    }
  };

  // Function to clear patients cache for a specific user or all users
  const clearPatientsCache = (uid = null) => {
    if (uid) {
      setPatientsCache((prev) => {
        const newCache = new Map(prev);
        newCache.delete(uid);
        return newCache;
      });
      setPatientsCacheTimestamp((prev) => {
        const newCache = new Map(prev);
        newCache.delete(uid);
        return newCache;
      });
    } else {
      setPatientsCache(new Map());
      setPatientsCacheTimestamp(new Map());
    }
  };

  // Function to force refresh patients data for a specific doctor
  const refreshPatientsData = async (doctorUid) => {
    if (!doctorUid) return;

    // Clear the cache for this doctor to force a fresh fetch
    clearPatientsCache(doctorUid);

    // Fetch fresh data
    const result = await getMyPatientsDetails(doctorUid);
    const patients = result ? result?.patients : [];

    // Update cache with fresh data
    const now = Date.now();
    setPatientsCache((prev) => new Map(prev).set(doctorUid, patients));
    setPatientsCacheTimestamp((prev) => new Map(prev).set(doctorUid, now));

    return patients;
  };

  const fetchAllTeacher = async (uid) => {
    const result = await fetchAllTeachers(uid);
    if (result) {
      return result?.students;
    } else {
      return [];
    }
  };

  const fetchTeachersForStudent = async (uid) => {
    const result = await fetchingTeachersForStudents(uid);
    if (result) {
      return result?.data;
    } else {
      return [];
    }
  };

  return (
    <DataContext.Provider
      value={{
        patientData,
        studentData,
        fetchPatientsForDoctor,
        currentPatientData,
        currentStudentData,
        fetchOnePatientData,
        fetchOneStudentData,
        currentPatientVideos,
        fetchOnePatientAllVideos,
        allPatientsAllVideos,
        fetchAllPatientsAllVideos,
        setCurrentPatientData,
        setCurrentStudentData,
        setPatientData,
        setStudentData,
        loadingFetchOnePatientData,
        loadingFetchOneStudentData,
        user,
        typeOfUser,
        resetStates,
        resetStudentStates,
        fetchAllTeacher,
        fetchTeachersForStudent,
        fetchAllStudentsAllVideos,
        fetchOneStudentAllVideos,
        clearVideosCache,
        clearPatientsCache,
        refreshPatientsData,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
